import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CpvData {
  description: string;
  category: string;
}

interface CpvBatchContextType {
  getCpvData: (code: string) => CpvData | null;
  isLoading: (code: string) => boolean;
  requestCpv: (code: string) => void;
}

const CpvBatchContext = createContext<CpvBatchContextType | null>(null);

// Global cache shared across all instances - keyed by language+code
const globalCache = new Map<string, CpvData | null>();

// Get current language from localStorage or default to 'en'
function getCurrentLanguage(): string {
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = localStorage.getItem('i18nextLng');
    if (stored) return stored.substring(0, 2);
  }
  return 'en';
}

export function CpvBatchProvider({ children }: { children: ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage);
  
  // Listen for language changes
  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for language changes
    const interval = setInterval(() => {
      const newLang = getCurrentLanguage();
      if (newLang !== currentLanguage) {
        setCurrentLanguage(newLang);
      }
    }, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentLanguage]);
  
  const [cache, setCache] = useState<Map<string, CpvData | null>>(new Map());
  const [loadingCodes, setLoadingCodes] = useState<Set<string>>(new Set());
  const pendingCodes = useRef<Set<string>>(new Set());
  const batchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Generate cache key with language
  const getCacheKey = useCallback((code: string) => `${currentLanguage}:${code}`, [currentLanguage]);

  const fetchBatch = useCallback(async (codes: string[]) => {
    if (codes.length === 0) return;

    // Mark codes as loading
    setLoadingCodes(prev => {
      const next = new Set(prev);
      codes.forEach(code => next.add(code));
      return next;
    });

    try {
      // First try to get translations in the current language
      const { data: translationData, error: translationError } = await supabase
        .from('cpv_translations')
        .select('code, description')
        .in('code', codes)
        .eq('language', currentLanguage);

      if (translationError) {
        console.error('Error fetching translations:', translationError);
      }

      // Create a map of translation results
      const translationsMap = new Map<string, string>();
      translationData?.forEach(item => {
        translationsMap.set(item.code, item.description);
      });

      // For codes not found in translations, try fallback to English
      const notFoundInTranslations = codes.filter(code => !translationsMap.has(code));
      
      if (notFoundInTranslations.length > 0 && currentLanguage !== 'en') {
        const { data: fallbackData } = await supabase
          .from('cpv_translations')
          .select('code, description')
          .in('code', notFoundInTranslations)
          .eq('language', 'en');

        fallbackData?.forEach(item => {
          if (!translationsMap.has(item.code)) {
            translationsMap.set(item.code, item.description);
          }
        });
      }

      // Fetch category data from cpv_codes table
      const { data: categoryData, error: categoryError } = await supabase
        .from('cpv_codes')
        .select('code, category')
        .in('code', codes);

      if (categoryError) {
        console.error('Error fetching categories:', categoryError);
      }

      // Create a map of category results
      const categoriesMap = new Map<string, string>();
      categoryData?.forEach(item => {
        categoriesMap.set(item.code, item.category);
      });

      // Combine results
      const resultsMap = new Map<string, CpvData>();
      codes.forEach(code => {
        const description = translationsMap.get(code);
        const category = categoriesMap.get(code);
        
        if (description || category) {
          resultsMap.set(code, {
            description: description || '',
            category: category || ''
          });
        }
      });

      // For codes not found, try prefix matching from cpv_codes
      const notFoundCodes = codes.filter(code => !resultsMap.has(code));
      
      if (notFoundCodes.length > 0) {
        const prefixes = [...new Set(notFoundCodes.map(code => code.substring(0, 2)))];
        
        const { data: prefixData } = await supabase
          .from('cpv_codes')
          .select('code, description, category')
          .or(prefixes.map(p => `code.ilike.${p}%`).join(','));

        const prefixMap = new Map<string, CpvData>();
        prefixData?.forEach(item => {
          const prefix = item.code.substring(0, 2);
          if (!prefixMap.has(prefix)) {
            prefixMap.set(prefix, {
              description: item.description,
              category: item.category
            });
          }
        });

        notFoundCodes.forEach(code => {
          const prefix = code.substring(0, 2);
          if (prefixMap.has(prefix)) {
            resultsMap.set(code, prefixMap.get(prefix)!);
          }
        });
      }

      // Update cache with language-specific keys
      setCache(prev => {
        const next = new Map(prev);
        codes.forEach(code => {
          const cacheKey = getCacheKey(code);
          const result = resultsMap.get(code) || null;
          next.set(cacheKey, result);
          globalCache.set(cacheKey, result);
        });
        return next;
      });

    } catch (error) {
      console.error('Error fetching CPV batch:', error);
      setCache(prev => {
        const next = new Map(prev);
        codes.forEach(code => {
          const cacheKey = getCacheKey(code);
          next.set(cacheKey, null);
          globalCache.set(cacheKey, null);
        });
        return next;
      });
    } finally {
      setLoadingCodes(prev => {
        const next = new Set(prev);
        codes.forEach(code => next.delete(code));
        return next;
      });
    }
  }, [currentLanguage, getCacheKey]);

  const requestCpv = useCallback((code: string) => {
    const cacheKey = getCacheKey(code);
    
    // Skip if already cached or pending
    if (globalCache.has(cacheKey) || pendingCodes.current.has(code)) {
      return;
    }

    pendingCodes.current.add(code);

    // Debounce batch requests
    if (batchTimeout.current) {
      clearTimeout(batchTimeout.current);
    }

    batchTimeout.current = setTimeout(() => {
      const codes = Array.from(pendingCodes.current);
      pendingCodes.current.clear();
      fetchBatch(codes);
    }, 50);
  }, [fetchBatch, getCacheKey]);

  const getCpvData = useCallback((code: string): CpvData | null => {
    const cacheKey = getCacheKey(code);
    return cache.get(cacheKey) || globalCache.get(cacheKey) || null;
  }, [cache, getCacheKey]);

  const isLoading = useCallback((code: string): boolean => {
    return loadingCodes.has(code) || pendingCodes.current.has(code);
  }, [loadingCodes]);

  return (
    <CpvBatchContext.Provider value={{ getCpvData, isLoading, requestCpv }}>
      {children}
    </CpvBatchContext.Provider>
  );
}

export function useCpvBatch() {
  const context = useContext(CpvBatchContext);
  if (!context) {
    throw new Error('useCpvBatch must be used within a CpvBatchProvider');
  }
  return context;
}

// Hook for individual CPV code
export function useCpvData(code: string) {
  const { getCpvData, isLoading, requestCpv } = useCpvBatch();

  useEffect(() => {
    requestCpv(code);
  }, [code, requestCpv]);

  return {
    data: getCpvData(code),
    isLoading: isLoading(code)
  };
}
